// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type {
  TourDefinition,
  TourKind,
  TourStep,
} from "../dto/tourModels";

const ONBOARDING_STEPS: readonly TourStep[] = [
  {
    id: "world-welcome",
    target: "[data-tour-step='welcome']",
    title: "About You",
    body: "Tell us your role and team size. This shapes which sections you see — for example property management appears only for property-focused work.",
    hint: "Choose your role and team size, then select Continue.",
  },
  {
    id: "world-contacts",
    target: "[data-tour-step='contacts']",
    title: "Your Contacts",
    body: "Your client and relationship list lives here. You can import your existing contacts or add them later — nothing is locked in.",
    hint: "Import or skip this step to keep moving.",
    details: [
      {
        trigger: "Plan your contact list",
        steps: [
          {
            id: "learn-onb-contacts-1",
            target: "[data-tour-step='contacts']",
            title: "Import or Add Later",
            body: "Bring in an existing contact list now, or add people later in the workspace Contacts page — nothing here is locked in.",
          },
        ],
      },
    ],
  },
  {
    id: "world-communications",
    target: "[data-tour-step='communications']",
    title: "Communications",
    body: "Connect how you reach people — Email, SMS, WhatsApp, and voice. Every channel you connect here is available across the workspace.",
    hint: "Select the channels you use, then Continue.",
    details: [
      {
        trigger: "Pick your channels",
        steps: [
          {
            id: "learn-onb-comm-1",
            target: "[data-tour-step='communications']",
            title: "Your Channels",
            body: "Choose Email, SMS, WhatsApp, or voice. Whatever you connect here is available everywhere in the workspace.",
          },
        ],
      },
    ],
  },
  {
    id: "world-properties",
    target: "[data-tour-step='properties']",
    title: "Properties",
    body: "For property work, this is where your portfolio and listings are managed and tracked.",
    hint: "Add properties or skip — you can manage them anytime.",
  },
  {
    id: "world-team",
    target: "[data-tour-step='team']",
    title: "Your Team",
    body: "Invite colleagues and set up permissions so the right people can work together.",
    hint: "Invite teammates or skip for now.",
  },
  {
    id: "world-done",
    target: "[data-tour-step='done']",
    title: "You’re all set",
    body: "That’s the setup complete. Next you’ll take a quick tour of the pages you’ll use every day, including workspace settings.",
    isFinal: true,
  },
];

const WORKSPACE_STEPS: readonly TourStep[] = [
  {
    id: "page-insights",
    routePath: "/insights",
    target: "[data-tour-step='page-insights']",
    title: "Daily Brief",
    body: "Your ranked priorities for the day — overdue, due today, awaiting reply. Select a card to open its full brief and act in place: complete, snooze, reply, or schedule.",
    hint: "Select an item to see its actions.",
    details: [
      {
        trigger: "Use the Daily Brief tabs",
        steps: [
          {
            id: "learn-insights-tabs-1",
            target: "[data-tour-step='page-insights']",
            title: "Pick a View",
            body: "The Daily Brief has three tabs — Daily Brief (your priorities), Prospecting, and Relationships. Select a tab to switch the day’s list by area.",
          },
          {
            id: "learn-insights-tabs-2",
            target: "[data-tour-step='page-insights']",
            title: "Open an Item",
            body: "Select any item on a list to open its full brief and act in place — complete, snooze, reply, or schedule — without leaving the page.",
          },
        ],
      },
      {
        trigger: "Work your task list",
        steps: [
          {
            id: "learn-insights-tasks-1",
            target: "[data-tour-step='page-insights']",
            title: "Priorities & Overdue",
            body: "The list ranks your tasks by urgency — overdue first, then due today, then awaiting reply. Each task shows its priority.",
          },
          {
            id: "learn-insights-tasks-2",
            target: "[data-tour-step='page-insights']",
            title: "Complete or Snooze",
            body: "Open a task and choose Complete to finish it, Snooze to push it to a later day, or Reply and Schedule to keep it moving.",
          },
        ],
      },
    ],
  },
  {
    id: "page-journeys",
    routePath: "/journeys",
    target: "[data-tour-step='page-journeys']",
    title: "Journeys",
    body: "Track client and property progression across Buying, Selling, and Leasing. Select a card to open that record’s workspace and take the next step.",
    details: [
      {
        trigger: "Use the Journeys tabs",
        steps: [
          {
            id: "learn-journeys-tabs-1",
            target: "[data-tour-step='page-journeys']",
            title: "Four Views",
            body: "Combined shows every journey; Buying, Selling, and Leasing each filter to one deal type and surface its own pipeline.",
          },
          {
            id: "learn-journeys-tabs-2",
            target: "[data-tour-step='page-journeys']",
            title: "Open a Record",
            body: "Select any journey card to open that record’s workspace and take the next step on the deal.",
          },
        ],
      },
      {
        trigger: "Journey stages",
        steps: [
          {
            id: "learn-journeys-stages-1",
            target: "[data-tour-step='page-journeys']",
            title: "Follow the Funnel",
            body: "Every journey moves through stages from lead to close — for example Searching, Fee Agreement, then Under Contract. The stage label tells you exactly where the deal is.",
          },
          {
            id: "learn-journeys-stages-2",
            target: "[data-tour-step='page-journeys']",
            title: "Advance a Stage",
            body: "Open the record and take the next step to move it forward. Stage transitions are described in plain language, like “They accepted an offer.”",
          },
        ],
      },
      {
        trigger: "Sub-tasks on a journey",
        steps: [
          {
            id: "learn-journeys-tasks-1",
            target: "[data-tour-step='page-journeys']",
            title: "Next Steps",
            body: "Each journey record carries a list of next tasks — follow-ups, documents, and actions. Work through them to move the deal along.",
          },
          {
            id: "learn-journeys-tasks-2",
            target: "[data-tour-step='page-journeys']",
            title: "Track Completion",
            body: "Mark each task done as you finish it so the record stays current and nothing falls through the cracks.",
          },
        ],
      },
      {
        trigger: "Start a journey",
        steps: [
          {
            id: "learn-start-journey-1",
            target: "[data-tour-step='learn-start-journey']",
            title: "Start a Journey",
            body: "Select Start a Journey to turn a prospect into an active client. Choose Buying, Selling, or Leasing, then add the property and price.",
          },
          {
            id: "learn-start-journey-2",
            target: "[data-tour-step='page-journeys']",
            title: "Work the Record",
            body: "The new journey appears as a card. Select it to open the record workspace, log notes and next steps, and drive the deal to close.",
          },
        ],
      },
    ],
  },
  {
    id: "page-prospecting",
    routePath: "/prospecting",
    target: "[data-tour-step='page-prospecting']",
    title: "Prospecting",
    body: "Manage leads, outreach, and conversion. Move prospects toward becoming active clients and start journeys from here.",
    details: [
      {
        trigger: "Move prospects to clients",
        steps: [
          {
            id: "learn-prospecting-1",
            target: "[data-tour-step='page-prospecting']",
            title: "Leads & Outreach",
            body: "Manage leads and log outreach here. Each prospect offers actions to call, email, or record a follow-up.",
          },
          {
            id: "learn-prospecting-2",
            target: "[data-tour-step='page-prospecting']",
            title: "Convert & Start",
            body: "When a prospect is ready, convert them into an active client and start a journey — Buying, Selling, or Leasing — right from here.",
          },
        ],
      },
    ],
  },
  {
    id: "page-relationships",
    routePath: "/relationships",
    target: "[data-tour-step='page-relationships']",
    title: "Relationships",
    body: "Client care and relationship management — nurture the people who matter most.",
    details: [
      {
        trigger: "Nurture a relationship",
        steps: [
          {
            id: "learn-relationships-1",
            target: "[data-tour-step='page-relationships']",
            title: "Client Care",
            body: "This page keeps the people who matter most front of mind — check-ins, reminders, and relationship milestones.",
          },
          {
            id: "learn-relationships-2",
            target: "[data-tour-step='page-relationships']",
            title: "Take an Action",
            body: "Open a person’s card to log a check-in, set a follow-up, or send the next message right from the record.",
          },
        ],
      },
    ],
  },
  {
    id: "page-communications",
    routePath: "/communications",
    target: "[data-tour-step='page-communications']",
    title: "Communications",
    body: "All messages across your connected channels in one place, with threads and templates.",
    details: [
      {
        trigger: "Use your inbox",
        steps: [
          {
            id: "learn-communications-1",
            target: "[data-tour-step='page-communications']",
            title: "One Conversation List",
            body: "Messages across every connected channel — Email, SMS, WhatsApp, voice — appear here as threads in one place.",
          },
          {
            id: "learn-communications-2",
            target: "[data-tour-step='page-communications']",
            title: "Reply & Templates",
            body: "Open a thread to reply in place, and reach for a saved template to send consistent, on-brand responses.",
          },
        ],
      },
    ],
  },
  {
    id: "page-contacts",
    routePath: "/contacts",
    target: "[data-tour-step='page-contacts']",
    title: "Contacts",
    body: "Your CRM and relationships — every person and company you work with. Add, import, and refine your contacts here.",
    hint: "Use New Contact or Import to grow your list.",
    details: [
      {
        trigger: "Add & manage contacts",
        steps: [
          {
            id: "learn-contacts-add-1",
            target: "[data-tour-step='page-contacts']",
            title: "Add a Contact",
            body: "Select New Contact to create a person or company. Fill in the essentials — name, email, phone, company — and save.",
          },
          {
            id: "learn-contacts-add-2",
            target: "[data-tour-step='page-contacts']",
            title: "Search & Deduplicate",
            body: "Use the search bar to find anyone, and Find Duplicates to merge records for the same person so your CRM stays clean.",
          },
        ],
      },
      {
        trigger: "Learn how to import contacts",
        steps: [
          {
            id: "learn-import-1",
            target: "[data-tour-step='learn-import']",
            title: "Open Import",
            body: "Select the Import button to open the import panel and choose a CSV or spreadsheet of your existing contacts.",
          },
          {
            id: "learn-import-2",
            target: "[data-tour-step='page-contacts']",
            title: "Map Your Columns",
            body: "In the panel, map each column of your file to a contact field — name, email, phone, company — and review the preview before importing.",
          },
          {
            id: "learn-import-3",
            target: "[data-tour-step='page-contacts']",
            title: "Review & Deduplicate",
            body: "After importing, use Find Duplicates to merge entries for the same person so your CRM stays clean.",
          },
        ],
      },
    ],
  },
  {
    id: "page-property",
    routePath: "/property",
    target: "[data-tour-step='page-property']",
    title: "Property",
    body: "Your portfolio and operations for the properties you manage.",
    details: [
      {
        trigger: "Manage your portfolio",
        steps: [
          {
            id: "learn-property-1",
            target: "[data-tour-step='page-property']",
            title: "Your Properties",
            body: "This page lists the portfolio and listings you manage, with each property’s status and operational detail at a glance.",
          },
          {
            id: "learn-property-2",
            target: "[data-tour-step='page-property']",
            title: "Track & Act",
            body: "Open a property to edit its details, check status, and see the work orders and activity tied to it.",
          },
        ],
      },
    ],
  },
  {
    id: "page-vendors",
    routePath: "/vendors",
    target: "[data-tour-step='page-vendors']",
    title: "Vendors",
    body: "Qualification and credentials for the vendors who support your work.",
    details: [
      {
        trigger: "Qualify a vendor",
        steps: [
          {
            id: "learn-vendors-1",
            target: "[data-tour-step='page-vendors']",
            title: "Vendor Records",
            body: "Keep the contractors who support your work here — with credentials, qualifications, and current status.",
          },
          {
            id: "learn-vendors-2",
            target: "[data-tour-step='page-vendors']",
            title: "Qualify & Assign",
            body: "Add or qualify a vendor, then assign them to a work order or delegation when the work comes up.",
          },
        ],
      },
    ],
  },
  {
    id: "page-workOrders",
    routePath: "/work-orders",
    target: "[data-tour-step='page-workOrders']",
    title: "Work Orders",
    body: "Maintenance and dispatch — track what needs doing and who’s doing it.",
    details: [
      {
        trigger: "Create & dispatch a work order",
        steps: [
          {
            id: "learn-workOrders-create-1",
            target: "[data-tour-step='page-workOrders']",
            title: "Log the Work",
            body: "Create a work order for maintenance or dispatch, picking the property and describing what needs doing.",
          },
          {
            id: "learn-workOrders-create-2",
            target: "[data-tour-step='page-workOrders']",
            title: "Assign a Doer",
            body: "Assign the work order to a vendor or team member so it’s clear who owns the job.",
          },
        ],
      },
      {
        trigger: "Track the sub-tasks",
        steps: [
          {
            id: "learn-workOrders-tasks-1",
            target: "[data-tour-step='page-workOrders']",
            title: "Status & Sub-Tasks",
            body: "Each work order tracks its own sub-tasks and status — open, in progress, completed — so you can see progress at a glance.",
          },
          {
            id: "learn-workOrders-tasks-2",
            target: "[data-tour-step='page-workOrders']",
            title: "Complete & Close",
            body: "Mark sub-tasks done as they finish, then complete the work order once the job is closed out.",
          },
        ],
      },
    ],
  },
  {
    id: "page-workloadForecasts",
    routePath: "/workload-forecasts",
    target: "[data-tour-step='page-workloadForecasts']",
    title: "Forecasts",
    body: "Workload collision forecasts to plan capacity ahead of time.",
    details: [
      {
        trigger: "Plan capacity",
        steps: [
          {
            id: "learn-forecasts-1",
            target: "[data-tour-step='page-workloadForecasts']",
            title: "Spot Collisions",
            body: "Forecasts highlight where planned work overlaps, so you can spot a capacity problem before it happens.",
          },
          {
            id: "learn-forecasts-2",
            target: "[data-tour-step='page-workloadForecasts']",
            title: "Adjust the Plan",
            body: "Reschedule or rebalance assignments to smooth the load across your team and keep delivery on time.",
          },
        ],
      },
    ],
  },
  {
    id: "page-delegations",
    routePath: "/delegations",
    target: "[data-tour-step='page-delegations']",
    title: "Delegations",
    body: "Hand work to assistants and vendors, and track what’s been delegated.",
    details: [
      {
        trigger: "Learn how to offer a delegation",
        steps: [
          {
            id: "learn-offer-1",
            target: "[data-tour-step='learn-offer-delegation']",
            title: "Offer a Delegation",
            body: "Select Offer Delegation to hand a piece of work to an assistant, agent, vendor, or approved automation.",
          },
          {
            id: "learn-offer-2",
            target: "[data-tour-step='page-delegations']",
            title: "Track the Handoff",
            body: "Choose who, what, and a due date. Watch the status move through offered and accepted, then complete — or recall it if plans change.",
          },
        ],
      },
      {
        trigger: "Handle an incoming delegation",
        steps: [
          {
            id: "learn-delegations-accept-1",
            target: "[data-tour-step='page-delegations']",
            title: "Accept or Decline",
            body: "When work is offered to you, accept it to take it on, or decline it so the owner can reassign.",
          },
          {
            id: "learn-delegations-accept-2",
            target: "[data-tour-step='page-delegations']",
            title: "Complete or Recall",
            body: "Complete the work when it’s done — or, if you sent it, recall it before it’s accepted to change course.",
          },
        ],
      },
    ],
  },
  {
    id: "page-documents",
    routePath: "/documents",
    target: "[data-tour-step='page-documents']",
    title: "Documents",
    body: "Templates and generated PDFs for the documents you send.",
    details: [
      {
        trigger: "Generate a document",
        steps: [
          {
            id: "learn-documents-1",
            target: "[data-tour-step='page-documents']",
            title: "Pick a Template",
            body: "Choose a template for the document you need — letters, agreements, and reports.",
          },
          {
            id: "learn-documents-2",
            target: "[data-tour-step='page-documents']",
            title: "Fill & Send",
            body: "Complete the template, generate the PDF, and send or download it to the right contact.",
          },
        ],
      },
    ],
  },
  {
    id: "settings-workspace",
    routePath: "/settings",
    target: "[data-tour-step='settings-workspace']",
    title: "Workspace Settings",
    body: "Manage tenant identity, imports, connectors, team, and communication templates here — everything you set up during onboarding, editable anytime.",
    hint: "Select the Workspace tab, then continue.",
    details: [
      {
        trigger: "Config that lives here",
        steps: [
          {
            id: "learn-settings-1",
            target: "[data-tour-step='settings-workspace']",
            title: "Imports & Connectors",
            body: "Manage data imports and the connector tokens that keep your workspace in sync.",
          },
          {
            id: "learn-settings-2",
            target: "[data-tour-step='settings-workspace']",
            title: "Team & Templates",
            body: "Invite team members and tune your communication templates — all editable here anytime.",
          },
        ],
      },
    ],
  },
  {
    id: "settings-profile",
    routePath: "/settings",
    target: "[data-tour-step='settings-profile']",
    title: "Profile",
    body: "Your account identity and member-facing details.",
  },
  {
    id: "settings-appearance",
    routePath: "/settings",
    target: "[data-tour-step='settings-appearance']",
    title: "Appearance",
    body: "Choose the visual style of your workspace.",
  },
  {
    id: "settings-preferences",
    routePath: "/settings",
    target: "[data-tour-step='settings-preferences']",
    title: "Preferences",
    body: "Notifications, reminders, and day-to-day workspace behavior. You can also reopen this tour from here anytime.",
  },
  {
    id: "world-tour-done",
    target: "[data-tour-step='world-tour-done']",
    title: "You’re Ready",
    body: "That’s every page and the workspace settings. Use the Learn how… guides on any page to dive deeper, and reopen this tour anytime from the help menu.",
    isFinal: true,
  },
];

const TOUR_ONBOARDING: TourDefinition = {
  kind: "onboarding",
  title: "Set Up Your Workspace",
  steps: ONBOARDING_STEPS,
};

const TOUR_WORKSPACE: TourDefinition = {
  kind: "workspace",
  title: "Tour the Workspace",
  steps: WORKSPACE_STEPS,
};

const TEAM_ONLY_STEP_IDS: ReadonlySet<string> = new Set(["world-team", "page-delegations"]);

const SOLO_STEP_COPY: Readonly<Record<string, Pick<TourStep, "body"> & Partial<Pick<TourStep, "title">>>> = {
  "learn-vendors-2": {
    body: "Add or qualify a vendor, then assign them to a work order when the work comes up.",
  },
  "learn-workOrders-create-2": {
    body: "Assign the work order to a vendor so it’s clear who owns the job.",
  },
  "learn-forecasts-2": {
    body: "Reschedule or rebalance work to smooth the load and keep delivery on time.",
  },
  "settings-workspace": {
    body: "Manage tenant identity, imports, connectors, and communication templates here — everything you set up during onboarding, editable anytime.",
  },
  "learn-settings-2": {
    title: "Templates",
    body: "Tune your communication templates — all editable here anytime.",
  },
};

function toSoloStep(step: TourStep): TourStep {
  return {
    ...step,
    ...SOLO_STEP_COPY[step.id],
    ...(step.details !== undefined
      ? {
          details: step.details.map((detail) => ({
            ...detail,
            steps: detail.steps.map(toSoloStep),
          })),
        }
      : {}),
  };
}

function toSoloTour(tour: TourDefinition): TourDefinition {
  return {
    ...tour,
    steps: tour.steps.filter((step) => !TEAM_ONLY_STEP_IDS.has(step.id)).map(toSoloStep),
  };
}

const SOLO_TOUR_ONBOARDING = toSoloTour(TOUR_ONBOARDING);
const SOLO_TOUR_WORKSPACE = toSoloTour(TOUR_WORKSPACE);

export function getTour(kind: TourKind, teamsEnabled: boolean): TourDefinition {
  if (kind === "onboarding") {
    return teamsEnabled ? TOUR_ONBOARDING : SOLO_TOUR_ONBOARDING;
  }
  return teamsEnabled ? TOUR_WORKSPACE : SOLO_TOUR_WORKSPACE;
}
